// A name, and the one menu of what can be done with what it names (#2344).
//
// `EntityRef` is the name — `ObjectLink`'s for a device, a client or an SSID,
// unchanged: a click opens it, ⌘-click narrows this page to it (#263) — and a
// button beside it that opens the acts `entityActions` lists. On a desktop the
// button is a small ▾, shown in a table when its row is hovered or holds focus
// and always elsewhere, and the menu is the shared `Popover`. On a touch screen,
// or at the shell's phone width, the button is a ⋯ a thumb can hit and the menu
// is a sheet from the bottom on the shared `Dialog`. Right-click, Shift+F10, the
// context-menu key and "." on the name open the same menu; so does a long press
// on a touch screen, which is never the only way to it.
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { canAnywhere } from "../authz";
import { copyValue, shortValue } from "../copy";
import { CopyIcon, Glyph, MoreIcon, OpenIcon } from "../icons";
import { Dialog } from "../surfaces";
import { Id, Popover } from "../ui";
import { entityActions, KIND_WORD, nameOf, primaryOf, type Entity, type Line, type Target } from "./entity";
import { useEstate } from "./estate";
import { facetSearch, useFacets } from "./facets";
import { ObjectLink } from "./ObjectLink";
import { ScopeLink, ScopePickLink, scopeSearch, useScope, type Scope } from "./scope";
import { useSession } from "./session";

/** Another object at the same level, offered beside the menu's own acts — the
 * breadcrumb's siblings: the other sites of a customer, the other customers of
 * an organization. */
export interface Sibling { name: string; scope: Scope; current: boolean }

const COARSE = "(pointer: coarse)";
const PHONE = "(max-width: 760px)";

/** Whether the menu is a sheet: a coarse pointer, or the shell's phone width.
 * Decided by the input as well as by the width, so a tablet gets the sheet and
 * a narrow desktop window keeps the popover only when it is not phone-narrow. */
export function useSheet(): boolean {
  const read = () => typeof window !== "undefined" && typeof window.matchMedia === "function"
    && (window.matchMedia(COARSE).matches || window.matchMedia(PHONE).matches);
  const [sheet, setSheet] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const lists = [window.matchMedia(COARSE), window.matchMedia(PHONE)];
    const on = () => setSheet(read());
    lists.forEach((m) => m.addEventListener?.("change", on));
    return () => lists.forEach((m) => m.removeEventListener?.("change", on));
  }, []);
  return sheet;
}

/** The name, drawn the way the page already draws it for the three kinds
 * `ObjectLink` knows, a site as the way to its page, and the rest as text. */
function DefaultName({ entity: e }: { entity: Entity }) {
  switch (e.kind) {
    case "device": return <ObjectLink kind="device" value={e.serial}>{nameOf(e)}</ObjectLink>;
    case "station": return <ObjectLink kind="station" value={e.mac.toLowerCase().replace(/[^0-9a-f]/g, "")}>{nameOf(e)}</ObjectLink>;
    case "ssid": return <ObjectLink kind="ssid" value={e.ssid} />;
    case "site": return <ScopeLink to={`/sites/${e.id}`} facets={{}}>{e.name}</ScopeLink>;
    default: return <span>{e.name}</span>;
  }
}

/** A name and its menu. `children` is the name as the page draws it; absent,
 * the default for the kind. `always` shows the button outside a table row's
 * hover, as on a detail page's title and in a sentence. */
export function EntityRef({ entity, children, always = false, siblings, label }: {
  entity: Entity; children?: ReactNode; always?: boolean; siblings?: { title: string; items: Sibling[] }; label?: string;
}) {
  const m = useEntityMenu(entity, siblings, label);
  return (
    // A wrapper, not a control: the name and the button inside it are the controls,
    // and it only hears their right-click, keys and long press.
    <span role="presentation" className={"eref" + (always ? " eref-always" : "")} onContextMenu={m.onContextMenu} onKeyDown={m.onKeyDown}
      onPointerDown={m.onPointerDown} onPointerUp={m.cancelPress} onPointerCancel={m.cancelPress} onPointerMove={m.onPointerMove}
      onClickCapture={m.onClickCapture}>
      {children ?? <DefaultName entity={entity} />}
      {m.button}
      {m.menu}
    </span>
  );
}

/** A device's name, drawn as `ObjectLink` draws it, with its menu (#2348): the
 * one form a page naming a device by its serial uses. `children` is the name
 * as the page draws it, and `name` what the menu's head calls it, the serial
 * where it has none. Its site is the scope's unless the page says otherwise. */
export function DeviceRef({ serial, name, isSwitch, siteId, children, ...link }: Omit<Parameters<typeof ObjectLink>[0], "kind" | "value"> & {
  serial: string; name?: string | null; isSwitch?: boolean; siteId?: string;
}) {
  const { scope } = useScope();
  return (
    <EntityRef entity={{ kind: "device", serial, name: name || undefined, switch: isSwitch, siteId: siteId ?? scope.siteId }}>
      <ObjectLink kind="device" value={serial} {...link}>{children ?? (name || serial)}</ObjectLink>
    </EntityRef>
  );
}

/** An SSID's name, drawn as `ObjectLink` draws it, with its menu (#2348). */
export function SsidRef({ ssid, networkId, siteId, children }: { ssid: string; networkId?: string; siteId?: string; children?: ReactNode }) {
  const { scope } = useScope();
  return (
    <EntityRef entity={{ kind: "ssid", ssid, networkId, siteId: siteId ?? scope.siteId }}>
      <ObjectLink kind="ssid" value={ssid}>{children ?? ssid}</ObjectLink>
    </EntityRef>
  );
}

/** An identifier shown beside a name that has the menu — a serial under a
 * device's name, a MAC in its drawer, a site's ID on its page (#2367). It is
 * the same object's menu, named for the identifier so a reader of the page can
 * tell the two buttons apart. `copy`, where the page had it, keeps the
 * identifier's own copy button beside it (#2344, UX-003): one click, and
 * *Copied* said where it was clicked. */
export function IdRef({ entity, value, always, full, copy }: { entity: Entity; value: string; always?: boolean; full?: boolean; copy?: string }) {
  return <EntityRef entity={entity} always={always} label={`Menu for ${value}`}><Id value={value} full={full} copy={copy} /></EntityRef>;
}

/** The menu's button alone, beside a name drawn elsewhere: a page's title. */
export function EntityMenuButton({ entity, siblings, label }: { entity: Entity; siblings?: { title: string; items: Sibling[] }; label?: string }) {
  const m = useEntityMenu(entity, siblings, label);
  return <span className="eref eref-always">{m.button}{m.menu}</span>;
}

/** The button and the menu for `entity`, and the handlers that open it from
 * the name: right-click, the keyboard's context-menu keys, a long press. */
export function useEntityMenu(entity: Entity, siblings?: { title: string; items: Sibling[] }, label?: string) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const sheet = useSheet();
  const name = label ?? `Menu for ${nameOf(entity)}`;
  const press = useRef<{ timer?: ReturnType<typeof setTimeout>; x: number; y: number; fired: boolean }>({ x: 0, y: 0, fired: false });

  const show = () => setOpen(true);
  const cancelPress = () => { if (press.current.timer) clearTimeout(press.current.timer); press.current.timer = undefined; };
  const onContextMenu = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest(".eref-menu")) return;
    e.preventDefault();
    show();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (open) return;
    if (e.key === "ContextMenu" || (e.shiftKey && e.key === "F10") || (e.key === "." && !e.metaKey && !e.ctrlKey && !e.altKey)) {
      e.preventDefault();
      show();
    }
  };
  // A long press on a touch screen: half a second still, then the menu; a move
  // of more than a few pixels is a scroll and cancels it, and the click the
  // finger's lift would send is swallowed so the name does not also open.
  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType !== "touch" || (e.target as HTMLElement).closest(".eref-menu")) return;
    press.current = { x: e.clientX, y: e.clientY, fired: false, timer: setTimeout(() => { press.current.fired = true; show(); }, 500) };
  };
  const onPointerMove = (e: PointerEvent) => {
    if (press.current.timer && Math.hypot(e.clientX - press.current.x, e.clientY - press.current.y) > 8) cancelPress();
  };
  const onClickCapture = (e: MouseEvent) => {
    if (!press.current.fired) return;
    press.current.fired = false;
    e.preventDefault();
    e.stopPropagation();
  };
  useEffect(() => cancelPress, []);

  const button = (
    <button ref={anchor} type="button" className={"btn btn-quiet btn-icon eref-menu" + (sheet ? " eref-touch" : "")}
      aria-haspopup="menu" aria-expanded={open} aria-label={name} title="Copy, scope, narrow or open"
      onClick={() => setOpen((o) => !o)}>
      <Glyph icon={sheet ? MoreIcon : OpenIcon} size={sheet ? 16 : 12} />
    </button>
  );
  const menu = open ? <EntityMenu entity={entity} siblings={siblings} anchor={anchor} sheet={sheet} label={name} onClose={() => setOpen(false)} /> : null;
  return { button, menu, onContextMenu, onKeyDown, onPointerDown, onPointerMove, cancelPress, onClickCapture };
}

/** The menu itself: a popover under the button, or a sheet from the bottom. */
function EntityMenu({ entity, siblings, anchor, sheet, label, onClose }: {
  entity: Entity; siblings?: { title: string; items: Sibling[] }; anchor: RefObject<HTMLButtonElement | null>; sheet: boolean; label: string; onClose(): void;
}) {
  const { pathname } = useLocation();
  const { scope } = useScope();
  // The menu asks for the estate the scope picker reads, where a site's customer
  // and organization are found; it is one shared copy, read once a session.
  const { lists } = useEstate();
  const { me } = useSession();
  const lines = entityActions(entity, { pathname, scope, lists, mayAudit: canAnywhere(me, "audit.read") });
  const done = () => { onClose(); if (!sheet) anchor.current?.focus(); };
  const primary = primaryOf(entity);
  const head = (
    <div className="entity-menu-head">
      <span className="entity-menu-kind">{KIND_WORD[entity.kind]}</span>
      <b>{nameOf(entity)}</b>
      <code className="id">{primary.what} {shortValue(primary.value)}</code>
    </div>
  );
  const body = <MenuBody lines={lines} siblings={siblings} label={label} done={done} />;
  if (sheet) {
    // In `document.body`, as the popover is: a name can sit in a table cell, and
    // neither the table's styles nor its row semantics belong to the sheet.
    return createPortal(
      <Dialog label={label} className="entity-sheet" onClose={onClose} backdropCloses>
        <div className="entity-sheet-grab" aria-hidden="true" />
        {head}
        {body}
        <button type="button" className="btn entity-sheet-cancel" onClick={onClose}>Cancel</button>
      </Dialog>,
      document.body,
    );
  }
  return (
    <Popover open anchor={anchor} onClose={onClose} label={label}>
      <div className="entity-menu">{head}{body}</div>
    </Popover>
  );
}

const GROUPS: [Line["group"], string][] = [["copy", "Copy"], ["narrow", "Narrow"], ["go", "Go to"]];

/** The lines, grouped, as menu items the arrow keys move between. */
function MenuBody({ lines, siblings, label, done }: { lines: Line[]; siblings?: { title: string; items: Sibling[] }; label: string; done(): void }) {
  const root = useRef<HTMLDivElement>(null);
  const { scope } = useScope();
  const { set } = useFacets();
  useEffect(() => { root.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus({ preventScroll: true }); }, []);
  const onKey = (e: KeyboardEvent) => {
    const items = [...(root.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => { e.preventDefault(); items[(i + items.length) % items.length]!.focus(); };
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(items.length - 1);
  };
  const href = (t: Target) => ({ pathname: t.pathname, search: facetSearch(t.facets, scopeSearch(t.scope ?? scope)) });
  return (
    <div ref={root} role="menu" aria-label={label} className="entity-menu-body" tabIndex={-1} onKeyDown={onKey}>
      {siblings && siblings.items.length > 1 && (
        <div role="group" aria-label={siblings.title}>
          <div className="entity-menu-group">{siblings.title}</div>
          {siblings.items.map((s) => (
            <ScopePickLink key={s.scope.siteId ?? s.scope.customerId ?? s.scope.orgId} scope={s.scope} role="menuitem" className="entity-menu-item" aria-current={s.current || undefined} onClick={done}>
              <span>{s.name}</span>{s.current && <span className="entity-menu-hint">current</span>}
            </ScopePickLink>
          ))}
        </div>
      )}
      {GROUPS.map(([group, title]) => {
        const these = lines.filter((l) => l.group === group);
        if (these.length === 0) return null;
        return (
          <div key={group} role="group" aria-label={title}>
            <div className="entity-menu-group">{title}</div>
            {these.map((l) => {
              if (l.group === "copy") {
                const value = "value" in l ? l.value : window.location.origin + href(l.link).pathname + href(l.link).search;
                return (
                  <button key={l.key} type="button" role="menuitem" className="entity-menu-item" onClick={() => { done(); void copyValue(value, l.what); }}>
                    <Glyph icon={CopyIcon} size={12} /><span>{l.label}</span>
                    {"value" in l && <span className="entity-menu-value" title={l.value}>{shortValue(l.value)}</span>}
                  </button>
                );
              }
              if (l.group === "narrow" && l.key === "filter") {
                return (
                  <button key={l.key} type="button" role="menuitem" className="entity-menu-item" onClick={() => { done(); set(l.facets); }}>
                    <span>{l.label}</span><span className="entity-menu-hint">{l.hint}</span>
                  </button>
                );
              }
              if (l.group === "narrow" && "pick" in l) {
                return (
                  <ScopePickLink key={l.key} scope={l.pick} role="menuitem" className="entity-menu-item" onClick={done}>
                    <span>{l.label}</span>{l.value && <span className="entity-menu-value">{l.value}</span>}
                  </ScopePickLink>
                );
              }
              if (l.group === "go") {
                return (
                  <Link key={l.key} to={href(l.to)} role="menuitem" className="entity-menu-item" onClick={done}>
                    <span>{l.label}</span>{l.value && <span className="entity-menu-value">{l.value}</span>}
                  </Link>
                );
              }
              return null;
            })}
          </div>
        );
      })}
    </div>
  );
}
